// Cache identities for the `dictation/preferences` family (ARCFE-1043 · C).
//
// Both are PROMOTIONS, not file moves: each literal was declared inside its own
// query hook's module scope, which the C step's key-factory freeze exists to
// end. Both old sites now import the name from this door and re-export it, so
// each identity has exactly one declaration.
//
// WHY THIS IS A SEPARATE MODULE FROM `logs-query-keys.ts`. The two-family
// partition ruling (the program owner, 2026-08-13) cuts `dictation` into
// `dictation/logs` and `dictation/preferences`, and three independent signals
// agree on that line:
//
//   1. RESOURCE — `/dictations**` (t11nClient) vs `/dictation/app-categories`
//      and `/dictation/verbal-commands-languages` (`scribeHttpV2`).
//   2. CACHE IDENTITY — nothing in this file is read, written or invalidated by
//      any logs surface, and nothing in `logs-query-keys.ts` is touched by a
//      preferences surface. Re-verified at HEAD for this PR.
//   3. API MODULE — the logs family's five fns are a tail block inside
//      `@/lib/SessionRecordingProvider/t11nClient.ts`; this family's three live
//      in `src/features/dictationLogs/api.ts` and share no transport with them.
//
// FALSIFIER: if a later measure shows the logs and preferences surfaces sharing
// a cache identity or an api module, the partition re-merges.

/**
 * Per-app-category post-processing tones. Read by
 * `useQueryDictationAppCategories`, optimistically written and then invalidated
 * by `useMutationUpdateDictationAppCategoryTone`, and invalidated by
 * `useDesktopDictationHotkey` — all at arity 1, `[KEY]`.
 */
export const DICTATION_APP_CATEGORIES_KEY = 'dictation-app-categories';

/**
 * Base ISO 639-1 codes that support verbal-command recognition.
 *
 * The literal is `'GetVerbalCommandsLanguages'` — PascalCase, unlike every
 * other identity in this package. That is the measured pre-move value and it is
 * frozen as-is: renaming it to match the house style would cold-start the cache
 * for every clinician mid-session, which is precisely what the freeze exists to
 * prevent. It is a rename for a later PR with a migration story, never a rider
 * on a move.
 *
 * NOTE FOR WHOEVER AUDITS THIS FILE WITH THE TOOLING: the shared cache-key
 * detector (`packages/eslint-plugin-rules/cache-key-shape.js`) CANNOT SEE this
 * declaration. Its `KEY_NAME` test requires the binding to end in a
 * `Key`/`Keys` token, and `GET_VERBAL_COMMANDS_LANGUAGES` does not — so this
 * identity is absent from `cache-key-baseline.json`, was invisible to the
 * census's key count, and is NOT protected by the `key-single-site` duplicate
 * check. The characterization test beside this file is its only guard. Measured
 * for ARCFE-1043 by running `scanSource` over this exact file.
 */
export const GET_VERBAL_COMMANDS_LANGUAGES = 'GetVerbalCommandsLanguages';
